import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'

/**
 * Reseller Courses (super-admin oversight) — every course a reseller contributed,
 * with attribution + sharing state (INCLUDING opted-out ones the reseller-facing
 * marketplace hides) and how many other resellers use it. Lets the platform
 * correct mis-attributed courses (the backfill was a heuristic).
 */
export default function ResellerCourses() {
  const [courses, setCourses] = useState([])
  const [resellers, setResellers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [savingId, setSavingId] = useState(null)

  const [search, setSearch] = useState('')
  const [resellerFilter, setResellerFilter] = useState('')
  const [sharedFilter, setSharedFilter] = useState('all') // all | shared | unshared

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get('/admin/course-integrations/reseller-courses')
      .then(r => { setCourses(r.data.courses || []); setResellers(r.data.resellers || []) })
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }
  useEffect(reload, [])

  async function reassign(course, resellerAccountId) {
    setSavingId(course.coursesId)
    try {
      await tournamentApi.patch(`/admin/course-integrations/reseller-courses/${course.coursesId}/attribution`,
        { resellerAccountId: resellerAccountId === '' ? null : Number(resellerAccountId) })
      reload()
    } catch (err) { alert(err?.response?.data?.message || err.message) }
    finally { setSavingId(null) }
  }

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase()
    return courses.filter(c =>
      (!resellerFilter || String(c.addedByResellerAccountId) === resellerFilter) &&
      (sharedFilter === 'all' || (sharedFilter === 'shared' ? c.shared : !c.shared)) &&
      (!s || `${c.courseName} ${c.city || ''} ${c.addedByResellerName || ''}`.toLowerCase().includes(s))
    )
  }, [courses, search, resellerFilter, sharedFilter])

  const sharedCount = courses.filter(c => c.shared).length

  return (
    <AdminShell title="Reseller Courses">
      <p style={{ color: '#64748b', margin: '0 0 20px', maxWidth: 780, lineHeight: 1.5 }}>
        Every course a reseller added, with who contributed it and whether it's shared with other
        resellers (opted-out courses are shown here too). Use <b>Reassign</b> to fix mis-attributed
        courses — set it to <b>Platform (InteGolf)</b> to make it a platform course (always shared,
        no owner control).
      </p>

      {loading ? (
        <div style={empty}>Loading…</div>
      ) : error ? (
        <div style={{ ...empty, color: '#b91c1c', borderColor: '#fecaca', background: '#fef2f2' }}>{error}</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search course, city, or reseller…"
              style={{ ...inp, flex: '1 1 240px', minWidth: 200, maxWidth: 360 }} />
            <select value={resellerFilter} onChange={e => setResellerFilter(e.target.value)} style={{ ...inp, width: 'auto' }}>
              <option value="">All resellers</option>
              {resellers.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
            </select>
            <select value={sharedFilter} onChange={e => setSharedFilter(e.target.value)} style={{ ...inp, width: 'auto' }}>
              <option value="all">All sharing</option>
              <option value="shared">Shared only</option>
              <option value="unshared">Opted out only</option>
            </select>
            <span style={{ fontSize: 13, color: '#64748b', marginLeft: 'auto' }}>
              {filtered.length} of {courses.length} · {sharedCount} shared
            </span>
          </div>

          {filtered.length === 0 ? (
            <div style={empty}>No reseller-contributed courses match.</div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                    <th style={th}>Course</th>
                    <th style={th}>Added by</th>
                    <th style={th}>Integration</th>
                    <th style={th}>Sharing</th>
                    <th style={th}>Other users</th>
                    <th style={th}>Reassign attribution</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => (
                    <tr key={c.coursesId} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={td}>
                        <strong style={{ color: '#0f172a' }}>{c.courseName}</strong>
                        {c.city ? <div style={{ fontSize: 12, color: '#64748b' }}>{c.city}</div> : null}
                        {!c.isResellerActive && <span style={{ ...badge, background: '#f1f5f9', color: '#64748b' }}>inactive</span>}
                      </td>
                      <td style={td}>{c.addedByResellerName || '—'}</td>
                      <td style={{ ...td, fontSize: 12, color: '#475569' }}>{c.integrationProvider || '—'}</td>
                      <td style={td}>
                        {c.shared
                          ? <span style={{ ...badge, background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' }}>Shared</span>
                          : <span style={{ ...badge, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>
                              Opted out{c.removalDate ? ` · removes ${c.removalDate}` : ''}
                            </span>}
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>{c.otherResellerUsage}</td>
                      <td style={td}>
                        <select
                          disabled={savingId === c.coursesId}
                          value={String(c.addedByResellerAccountId || '')}
                          onChange={e => reassign(c, e.target.value)}
                          style={{ ...inp, width: 'auto', minWidth: 180 }}>
                          <option value="">Platform (InteGolf)</option>
                          {resellers.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </AdminShell>
  )
}

const inp = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, background: '#fff' }
const th = { padding: '10px 12px', fontSize: 12, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }
const td = { padding: '10px 12px', color: '#334155', verticalAlign: 'top' }
const badge = { display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, marginTop: 4 }
const empty = { padding: '40px 20px', textAlign: 'center', color: '#94a3b8', border: '1px dashed #e2e8f0', borderRadius: 12, background: '#fafafa', fontSize: 14 }
